import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogCloseTrigger,
  Portal,
  VStack,
  Button,
  Text,
  Spinner,
  Box,
  Icon,
} from '@chakra-ui/react'
import { LuX } from 'react-icons/lu'
import { createPollVote, getPoll, logger } from '@kuow/core'
import { useWidgetOverlay, WidgetOverlayPortal } from '../../contexts/WidgetOverlayContext.js'

interface PollOption {
  id: number
  content: string
}

interface Poll {
  id: number
  title: string
  prompt: string
  options: PollOption[]
  thankYouMessage?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getString = (value: unknown): string | null => (typeof value === 'string' ? value : null)
const getNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const parseOptions = (rawOptions: unknown): PollOption[] => {
  if (!Array.isArray(rawOptions)) return []

  return rawOptions
    .map((item): PollOption | null => {
      if (!isRecord(item)) return null

      const id = getNumber(item.id)
      const content =
        getString(item.content) ??
        getString(item.label) ??
        getString(item.text) ??
        getString(item.answer) ??
        getString(item.title) ??
        (typeof item.name === 'string' ? item.name : null)

      if (id === null || !content) return null
      return { id, content }
    })
    .filter((o): o is PollOption => Boolean(o))
}

const formatPromptText = (value: string): string => {
  // API can include simple HTML like <br>; we render safely as text.
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?[^>]+>/g, '')
    .trim()
}

const parsePoll = (raw: unknown): Poll | null => {
  if (!isRecord(raw)) return null

  const id = getNumber(raw.id)
  const title = getString(raw.title) ?? 'Quick question'
  const prompt = getString(raw.prompt) ?? ''

  const optionSources: unknown[] = [raw.poll_options, raw.options, raw.choices, raw.answers, raw.responses]
  const options = optionSources
    .map((s) => parseOptions(s))
    .find((o) => o.length > 0) ?? []

  const thankYouMessage =
    getString(raw.thank_you_message) ??
    getString(raw.thankYouMessage) ??
    getString(raw.thank_you) ??
    undefined

  if (id === null || options.length === 0) {
    return null
  }

  return { id, title, prompt, options, thankYouMessage }
}

export interface PollDialogProps {
  pollId: number
  memberId: number
  donationId: number
}

type PollDialogStatus = 'loading' | 'ready' | 'submitting' | 'thank-you' | 'error'

export const PollDialog: React.FC<PollDialogProps> = ({ pollId, memberId, donationId }) => {
  const [open, setOpen] = useState(true)
  const [status, setStatus] = useState<PollDialogStatus>('loading')
  const [poll, setPoll] = useState<Poll | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null)
  const overlay = useWidgetOverlay()

  // Shadow DOM detection for proper portal rendering
  const portalContainerRef = React.useRef<HTMLDivElement | null>(null)
  const shadowProbeRef = React.useRef<HTMLSpanElement | null>(null)
  const [isShadowDom, setIsShadowDom] = React.useState(false)

  React.useEffect(() => {
    const rootNode = shadowProbeRef.current?.getRootNode()
    if (!(rootNode instanceof ShadowRoot)) return

    setIsShadowDom(true)
  }, [])

  // Ensure portal container gets font styling when it's created
  React.useEffect(() => {
    if (isShadowDom && !overlay && portalContainerRef.current) {
      portalContainerRef.current.style.fontFamily = 'var(--chakra-fonts-body)'
    }
  }, [isShadowDom, overlay])

  const promptText = useMemo(() => (poll?.prompt ? formatPromptText(poll.prompt) : ''), [poll?.prompt])
  const titleText = status === 'thank-you' ? 'Thanks for your feedback!' : promptText

  useEffect(() => {
    if (!open) return

    const controller = new AbortController()
    setStatus('loading')
    setError(null)
    setPoll(null)
    setSelectedOptionId(null)

    const load = async () => {
      try {
        const raw = await getPoll(String(pollId), { signal: controller.signal })
        const parsed = parsePoll(raw)
        if (!parsed) {
          setStatus('error')
          setError('Invalid poll response.')
          return
        }
        setPoll(parsed)
        setStatus('ready')
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return
        logger.error('Failed to load poll', { pollId, error: e })
        setStatus('error')
        setError(e instanceof Error ? e.message : 'Failed to load poll.')
      }
    }

    void load()

    return () => {
      controller.abort()
    }
  }, [open, pollId])

  const submitVote = useCallback(
    async (pollOptionId: number) => {
      if (!poll) return
      setSelectedOptionId(pollOptionId)
      setStatus('submitting')
      setError(null)

      try {
        await createPollVote(String(pollId), {
          poll_option_id: pollOptionId,
          member_id: memberId,
          donation_id: donationId,
        })
        setStatus('thank-you')
      } catch (e) {
        logger.error('Failed to submit poll vote', { pollId, memberId, donationId, pollOptionId, error: e })
        setStatus('ready')
        setError(e instanceof Error ? e.message : 'Failed to submit vote.')
      }
    },
    [poll, pollId, memberId, donationId],
  )

  if (!open) return null

  const portalContainer = overlay?.portalContainerRef ?? (isShadowDom ? portalContainerRef : undefined)

  return (
    <>
      <span ref={shadowProbeRef} style={{ display: 'none' }} />
      {isShadowDom && !overlay ? <div ref={portalContainerRef} /> : null}

      <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} size="sm">
        <Portal container={portalContainer}>
          {overlay ? (
            <WidgetOverlayPortal>
              <DialogBackdrop zIndex={2147483647} />
              <DialogPositioner zIndex={2147483647} px={{ base: '4', md: '6' }} py={{ base: '4', md: '8' }}>
                <DialogContent
                  fontFamily="body"
                  bg="bg.panel"
                  borderRadius="l3"
                  boxShadow="lg"
                  w="full"
                  maxW="sm"
                  p={6}
                  position="relative"
                >
                  <DialogHeader
                    p={0}
                    pt={status === 'thank-you' ? 6 : 0}
                    pb={6}
                    textAlign="center"
                  >
                    <DialogTitle
                      fontSize="xl"
                      fontWeight="semibold"
                      fontFamily="body"
                      color="fg"
                      lineHeight="1.1"
                      whiteSpace="pre-line"
                      textAlign="center"
                      w="full"
                    >
                      {titleText}
                    </DialogTitle>
                    <DialogCloseTrigger>
                      <Icon as={LuX} size="sm" />
                    </DialogCloseTrigger>
                  </DialogHeader>
                  <DialogBody p={0}>
                    {status === 'loading' && (
                      <VStack gap={4} py={6}>
                        <Spinner size="lg" color="teal.solid" />
                        <Text color="fg.muted" fontFamily="body">Loading…</Text>
                      </VStack>
                    )}

                    {status === 'error' && (
                      <Box bg="red.subtle" borderRadius="md" p={4}>
                        <Text fontWeight="semibold" color="red.fg" mb={1} fontFamily="body">
                          Something went wrong
                        </Text>
                        <Text color="red.fg" fontFamily="body">{error ?? 'Failed to load poll.'}</Text>
                      </Box>
                    )}

                    {(status === 'ready' || status === 'submitting') && poll && (
                      <VStack align="center" gap={6}>
                        {error && (
                          <Text color="red.fg" fontSize="sm" fontFamily="body">
                            {error}
                          </Text>
                        )}
                        
                        <VStack align="stretch" gap={2} w="full" maxW="200px">
                          {poll.options.map((o) => (
                            <Button
                              key={o.id}
                              variant="outline"
                              colorPalette="river"
                              onClick={() => void submitVote(o.id)}
                              disabled={status === 'submitting'}
                            >
                              {status === 'submitting' && selectedOptionId === o.id ? (
                                <Spinner size="sm" color="teal.solid" />
                              ) : null}
                              {o.content}
                            </Button>
                          ))}
                        </VStack>
                      </VStack>
                    )}

                    {status === 'thank-you' && (
                      <VStack gap={3} py={3} align="center">
                        <Button
                          variant="solid"
                          size="md"
                          colorPalette="river"
                          onClick={() => setOpen(false)}
                          fontFamily="body"
                          w="full"
                          maxW="200px"
                        >
                          Close
                        </Button>
                      </VStack>
                    )}
                  </DialogBody>
                </DialogContent>
              </DialogPositioner>
            </WidgetOverlayPortal>
          ) : (
            <>
              <DialogBackdrop zIndex={2147483647} />
              <DialogPositioner zIndex={2147483647} px={{ base: '4', md: '6' }} py={{ base: '4', md: '8' }}>
                <DialogContent
                  fontFamily="body"
                  bg="bg.panel"
                  borderRadius="l3"
                  boxShadow="lg"
                  w="full"
                  maxW="sm"
                  p={6}
                  position="relative"
                >
                  <DialogHeader
                    p={0}
                    pt={status === 'thank-you' ? 6 : 0}
                    pb={6}
                    textAlign="center"
                  >
                    <DialogTitle
                      fontSize="xl"
                      fontWeight="semibold"
                      fontFamily="body"
                      color="fg"
                      lineHeight="1.1"
                      whiteSpace="pre-line"
                      textAlign="center"
                      w="full"
                    >
                      {titleText}
                    </DialogTitle>
                    <DialogCloseTrigger>
                      <Icon as={LuX} size="sm" />
                    </DialogCloseTrigger>
                  </DialogHeader>
                  <DialogBody p={0}>
                    {status === 'loading' && (
                      <VStack gap={4} py={6}>
                        <Spinner size="lg" color="teal.solid" />
                        <Text color="fg.muted" fontFamily="body">Loading…</Text>
                      </VStack>
                    )}

                    {status === 'error' && (
                      <Box bg="red.subtle" borderRadius="md" p={4}>
                        <Text fontWeight="semibold" color="red.fg" mb={1} fontFamily="body">
                          Something went wrong
                        </Text>
                        <Text color="red.fg" fontFamily="body">{error ?? 'Failed to load poll.'}</Text>
                      </Box>
                    )}

                    {(status === 'ready' || status === 'submitting') && poll && (
                      <VStack align="center" gap={6}>
                        {error && (
                          <Text color="red.fg" fontSize="sm" fontFamily="body">
                            {error}
                          </Text>
                        )}
                        
                        <VStack align="stretch" gap={2} w="full" maxW="200px">
                          {poll.options.map((o) => (
                            <Button
                              key={o.id}
                              variant="outline"
                              colorPalette="river"
                              onClick={() => void submitVote(o.id)}
                              disabled={status === 'submitting'}
                            >
                              {status === 'submitting' && selectedOptionId === o.id ? (
                                <Spinner size="sm" color="teal.solid" />
                              ) : null}
                              {o.content}
                            </Button>
                          ))}
                        </VStack>
                      </VStack>
                    )}

                    {status === 'thank-you' && (
                      <VStack gap={3} py={3} align="center">
                        <Button
                          variant="solid"
                          size="md"
                          colorPalette="river"
                          onClick={() => setOpen(false)}
                          fontFamily="body"
                          w="full"
                          maxW="200px"
                        >
                          Close
                        </Button>
                      </VStack>
                    )}
                  </DialogBody>
                </DialogContent>
              </DialogPositioner>
            </>
          )}
        </Portal>
      </DialogRoot>
    </>
  )
}


